/**
 * RegisterWithInvite Component
 * User registration page using invitation token
 * Supports both new user registration and existing user org join
 *
 * MIGRATED: Custom CSS → Tailwind CSS (Mobile-First)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UserPlus, Mail, Lock, Phone, Check, AlertCircle, Eye, EyeOff, Building2, LogIn, Loader2 } from 'lucide-react';
import { userManagementAPI } from '@/services/api/userManagement';
import { config as productConfig } from '@/src/config';
import { Button } from '../ui/button';
import PhoneInput from '@/components/ui/PhoneInput';

import { useAppTranslation } from '@/contexts/TranslationContext';

interface InvitationInfo {
  email: string;
  name: string;
  organization_name: string;
  role: string;
  cbo_id: number;
  is_existing_user: boolean;
  phone_number?: string | null;
  // Per-invite 2FA policy. false = the admin exempted this invitee, so the
  // phone number is optional (2FA is off for them). undefined/null/true = 2FA
  // required, phone number mandatory.
  require_2fa?: boolean | null;
}

export const RegisterWithInvite: React.FC = () => {
  const { t } = useAppTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const invitationToken = searchParams.get('token');

  const [invitationInfo, setInvitationInfo] = useState<InvitationInfo | null>(null);
  const [verifying, setVerifying] = useState(true);

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    phone_number: '',
    terms_accepted: false,
    hipaa_accepted: false
  });

  // For existing user login flow
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loggingIn, setLoggingIn] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // BUG-006: backend verify-invitation returns a structured detail with a
  // `code` field — track it so the error page can render an actionable CTA
  // ("Sign in" for already_accepted; "Contact admin" for expired/cancelled).
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Verify invitation on mount
  useEffect(() => {
    if (!invitationToken) {
      setVerifying(false);
      setError('Invalid or missing invitation token');
      return;
    }
    const verify = async () => {
      try {
        const info = await userManagementAPI.verifyInvitation(invitationToken);
        setInvitationInfo(info);
        setLoginData(prev => ({ ...prev, email: info.email }));
        // If the admin pre-staged a personal mobile on the invitation,
        // pre-fill it on the registration form. The user can still edit it
        // before submitting — they may have moved numbers since the invite.
        if (info.phone_number) {
          setFormData(prev => ({ ...prev, phone_number: info.phone_number || '' }));
        }
      } catch (err: any) {
        // BUG-006: backend now returns structured detail
        // { code, message, ... } so we can distinguish "already accepted"
        // (signup completed, just sign in) from "expired" (link dead, need
        // a new invitation) and "cancelled" / "held". Russell Fairbanks
        // case at AngelFood: kept clicking the same invite after completing
        // registration, saw the generic "Invalid Invitation / has expired"
        // page, reported the link as expired.
        const detail = err.response?.data?.detail;
        if (detail && typeof detail === 'object' && detail.code) {
          setErrorCode(detail.code);
          setError(detail.message || 'Invitation cannot be used.');
        } else {
          setError(typeof detail === 'string' ? detail : 'Invalid or expired invitation');
        }
      } finally {
        setVerifying(false);
      }
    };
    verify();
  }, [invitationToken]);

  const getPasswordStrength = (password: string): { score: number; label: string; color: string } => {
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z\d]/.test(password)) score++;

    if (score <= 1) return { score, label: 'Weak', color: '#dc2626' };
    if (score <= 3) return { score, label: 'Fair', color: '#ea580c' };
    if (score <= 4) return { score, label: 'Good', color: '#ca8a04' };
    return { score, label: 'Strong', color: '#16a34a' };
  };

  const passwordStrength = getPasswordStrength(formData.password);

  // Handle new user registration (existing flow)
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!invitationToken) {
      setError('Invalid invitation token');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!formData.terms_accepted) {
      setError('You must accept the Terms of Service to continue');
      return;
    }

    if (!formData.hipaa_accepted) {
      setError('You must accept the HIPAA Agreement to continue');
      return;
    }

    // Phone is only mandatory when 2FA is required for this invite. An admin
    // can exempt the invitee (require_2fa === false), in which case they can
    // register without a phone.
    if (invitationInfo?.require_2fa !== false && (!formData.phone_number || formData.phone_number.trim() === '')) {
      setError('Phone number is required for two-factor authentication');
      return;
    }

    try {
      setSubmitting(true);
      await userManagementAPI.registerWithInvite({
        invitation_token: invitationToken,
        password: formData.password,
        phone_number: formData.phone_number,
        terms_accepted: formData.terms_accepted,
        hipaa_accepted: formData.hipaa_accepted
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/login?registered=true');
      }, 2000);
    } catch (err: any) {
      console.error('Registration failed:', err);
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
      setSubmitting(false);
    }
  };

  // Handle existing user joining a new org
  const handleJoinOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!invitationToken || !loginData.password) {
      setError('Please enter your password');
      return;
    }

    try {
      setLoggingIn(true);

      // Use the ABSOLUTE prod API base URL. A relative /api/... URL on
      // weavein.ai is same-origin and proxies to the DEV backend, so an
      // existing prod account would fail to authenticate here.
      const apiBase = (productConfig.weaveinApi.baseUrl ?? '').replace(/\/$/, '');

      // First, log in to get a JWT token
      const loginResponse = await fetch(`${apiBase}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginData.email, password: loginData.password }),
      });

      if (!loginResponse.ok) {
        const loginErr = await loginResponse.json().catch(() => ({}));
        throw new Error(loginErr.detail || 'Invalid email or password');
      }

      // The login endpoint returns `access_token` (not `token`). Reading the
      // wrong key sent `Bearer undefined` and silently failed the accept.
      const loginJson = await loginResponse.json();
      const accessToken = loginJson.access_token;

      // 2FA-enabled accounts don't get a token from step 1 — they need the
      // SMS/TOTP challenge (and any consent step) that the main login page
      // handles. Route them through the real login with a `next` back to the
      // authenticated accept page, which finishes the join once signed in.
      if (!accessToken) {
        const next = `/accept-org-invite?token=${encodeURIComponent(invitationToken)}`;
        navigate(`/login?next=${encodeURIComponent(next)}`);
        return;
      }

      // Then accept the org invitation using the authenticated token
      const acceptResponse = await fetch(`${apiBase}/api/auth/accept-org-invitation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ invitation_token: invitationToken }),
      });

      if (!acceptResponse.ok) {
        const acceptErr = await acceptResponse.json().catch(() => ({}));
        const detail = acceptErr.detail;
        throw new Error(
          (typeof detail === 'string' ? detail : detail?.message) || 'Failed to join organization'
        );
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/login?joined=true');
      }, 2000);
    } catch (err: any) {
      console.error('Join org failed:', err);
      setError(err.message || 'Failed to join organization. Please try again.');
      setLoggingIn(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-500 via-purple-500 to-purple-700 p-4 md:p-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl md:rounded-2xl p-6 md:p-10 w-full max-w-md shadow-2xl text-center">
          <Loader2 className="w-12 h-12 text-blue-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600 dark:text-gray-300">{t('auth.verifying_invitation', 'Verifying invitation...')}</p>
        </div>
      </div>
    );
  }

  // Invalid/expired token — BUG-006: differentiate by errorCode so users
  // who already completed registration get a "sign in" CTA, not a dead-end
  // "expired" page.
  if (!invitationToken || (!invitationInfo && error)) {
    const isAlreadyAccepted = errorCode === 'already_accepted';
    const headerKey = isAlreadyAccepted
      ? t('auth.already_registered', "You've already registered")
      : t('auth.invalid_invitation', 'Invalid Invitation');
    const ctaLabel = isAlreadyAccepted
      ? t('auth.sign_in_now', 'Sign in now')
      : t('auth.back_to_login', 'Back to Login');
    const iconColorClass = isAlreadyAccepted ? 'text-blue-600' : 'text-red-600';
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-500 via-purple-500 to-purple-700 p-4 md:p-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl md:rounded-2xl p-6 md:p-10 w-full max-w-md shadow-2xl text-center">
          {isAlreadyAccepted ? (
            <Check className={`w-16 h-16 ${iconColorClass} mx-auto mb-5`} />
          ) : (
            <AlertCircle className={`w-16 h-16 ${iconColorClass} mx-auto mb-5`} />
          )}
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {headerKey}
          </h2>
          <p className="text-sm md:text-base text-gray-600 dark:text-gray-300 mb-6">
            {error || t('auth.this_invitation_link_is_invalid', 'This invitation link is invalid or has expired.')}
          </p>
          <Button onClick={() => navigate('/login')} variant="primary" fullWidth>
            {ctaLabel}
          </Button>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-500 via-purple-500 to-purple-700 p-4 md:p-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl md:rounded-2xl p-6 md:p-10 w-full max-w-md shadow-2xl text-center">
          <div className="w-16 h-16 mx-auto mb-5 bg-green-500 rounded-full flex items-center justify-center">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {invitationInfo?.is_existing_user
              ? t('auth.organization_joined', 'Organization Joined!')
              : t('auth.account_created_successfully', 'Account Created Successfully!')}
          </h2>
          <p className="text-sm md:text-base text-gray-600 dark:text-gray-300">{t('auth.redirecting_you_to_login', 'Redirecting you to login...')}</p>
        </div>
      </div>
    );
  }

  // ========================================================================
  // EXISTING USER: Join Organization flow
  // ========================================================================
  if (invitationInfo?.is_existing_user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-500 via-purple-500 to-purple-700 p-4 md:p-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl md:rounded-2xl p-6 md:p-10 w-full max-w-md shadow-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <Building2 className="w-12 h-12 text-purple-600 dark:text-purple-400 mx-auto mb-4" />
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {t('auth.join_organization', 'Join Organization')}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              You&apos;ve been invited to join <strong>{invitationInfo.organization_name}</strong> as <strong>{invitationInfo.role}</strong>.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              Log in with your existing account to accept.
            </p>
          </div>

          <form onSubmit={handleJoinOrg} className="space-y-5">
            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-red-700 dark:text-red-400 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Email (read-only) */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                <Mail className="w-4 h-4" />
                <span>{t('auth.email', 'Email')}</span>
              </label>
              <div className="w-full px-3 py-2.5 md:py-3 border border-blue-200 dark:border-blue-600 rounded-lg text-sm md:text-base bg-blue-50 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100 font-medium">
                {invitationInfo.email}
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="login-password" className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                <Lock className="w-4 h-4" />
                <span>{t('auth.password', 'Password *')}</span>
              </label>
              <div className="relative">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={loginData.password}
                  onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder={t('auth.enter_your_password', 'Enter your password')}
                  required
                  disabled={loggingIn}
                  className="w-full px-3 pr-10 py-2.5 md:py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-50 dark:disabled:bg-gray-700 disabled:cursor-not-allowed bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={loggingIn || !loginData.password}
              loading={loggingIn}
              className="mt-6"
            >
              <LogIn className="w-4 h-4 mr-2" />
              {loggingIn ? 'Joining...' : `Join ${invitationInfo.organization_name}`}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
            <a href="/login" className="text-blue-600 dark:text-blue-400 font-medium hover:underline">
              {t('auth.back_to_login', 'Back to Login')}
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ========================================================================
  // NEW USER: Registration flow (existing behavior)
  // ========================================================================
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-500 via-purple-500 to-purple-700 p-4 md:p-6">
      <div className="bg-white dark:bg-gray-800 rounded-xl md:rounded-2xl p-6 md:p-10 w-full max-w-md shadow-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <UserPlus className="w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">{t('auth.create_your_account', 'Create Your Account')}</h1>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {invitationInfo
              ? `Join ${invitationInfo.organization_name} on WeaveIn.ai`
              : t('auth.complete_your_registration_to_join', 'Complete your registration to join WeaveIn.ai')}
          </p>
        </div>

        <form onSubmit={handleRegister} className="space-y-5">
          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-red-700 dark:text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Password Field */}
          <div>
            <label htmlFor="password" className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              <Lock className="w-4 h-4" />
              <span>{t('auth.password', 'Password *')}</span>
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                placeholder={t('auth.enter_a_strong_password', 'Enter a strong password')}
                required
                disabled={submitting}
                className="w-full px-3 pr-10 py-2.5 md:py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-50 dark:disabled:bg-gray-700 disabled:cursor-not-allowed bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {/* Password Strength Indicator */}
            {formData.password && (
              <div className="flex items-center gap-3 mt-2">
                <div className="flex-1 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full transition-all duration-300"
                    style={{
                      width: `${(passwordStrength.score / 5) * 100}%`,
                      backgroundColor: passwordStrength.color
                    }}
                  />
                </div>
                <span className="text-xs md:text-sm font-medium" style={{ color: passwordStrength.color }}>
                  {passwordStrength.label}
                </span>
              </div>
            )}
            <small className="block mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t('auth.minimum_8_characters_use_uppercase', 'Minimum 8 characters. Use uppercase, lowercase, numbers, and symbols.')}
            </small>
          </div>

          {/* Confirm Password Field */}
          <div>
            <label htmlFor="confirmPassword" className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              <Lock className="w-4 h-4" />
              <span>{t('auth.confirm_password', 'Confirm Password *')}</span>
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                placeholder={t('auth.reenter_your_password', 'Re-enter your password')}
                required
                disabled={submitting}
                className="w-full px-3 pr-10 py-2.5 md:py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-50 dark:disabled:bg-gray-700 disabled:cursor-not-allowed bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1"
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {formData.confirmPassword && formData.password !== formData.confirmPassword && (
              <small className="block mt-1 text-xs text-red-600 dark:text-red-400">{t('auth.passwords_do_not_match', 'Passwords do not match')}</small>
            )}
          </div>

          {/* Phone Number Field */}
          <div>
            <label htmlFor="phone" className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              <Phone className="w-4 h-4" />
              <span>
                {invitationInfo?.require_2fa === false
                  ? t('auth.phone_number_optional', 'Phone Number (optional)')
                  : t('auth.phone_number', 'Phone Number *')}
              </span>
            </label>
            <PhoneInput
              id="phone"
              value={formData.phone_number}
              onChange={(v) => setFormData(prev => ({ ...prev, phone_number: v }))}
              placeholder="+1 (555) 123-4567"
              required={invitationInfo?.require_2fa !== false}
              disabled={submitting}
              className="w-full px-3 py-2.5 md:py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-50 dark:disabled:bg-gray-700 disabled:cursor-not-allowed bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <small className="block mt-1 text-xs text-gray-500 dark:text-gray-400">
              {invitationInfo?.require_2fa === false
                ? t('auth.twofactor_disabled_phone_optional', 'Two-factor authentication is disabled for your account — a phone number is optional.')
                : t('auth.required_for_twofactor_authentication', 'Required for two-factor authentication')}
            </small>
          </div>

          {/* Agreements Section */}
          <div className="mt-8 p-5 bg-gray-50 dark:bg-gray-700 rounded-lg space-y-4">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">{t('auth.required_agreements', 'Required Agreements')}</h3>

            {/* Terms of Service Checkbox */}
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={formData.terms_accepted}
                onChange={(e) => setFormData(prev => ({ ...prev, terms_accepted: e.target.checked }))}
                disabled={submitting}
                required
                className="sr-only peer"
              />
              <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 rounded flex items-center justify-center peer-checked:bg-blue-600 peer-checked:border-blue-600 transition-colors flex-shrink-0 mt-0.5">
                {formData.terms_accepted && <Check className="w-3 h-3 text-white" />}
              </div>
              <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white">
                {t('auth.i_accept_the', 'I accept the')} <a href="/terms" target="_blank" className="text-blue-600 dark:text-blue-400 font-medium hover:underline">{t('auth.terms_of_service', 'Terms of Service')}</a> *
              </span>
            </label>

            {/* HIPAA Checkbox */}
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={formData.hipaa_accepted}
                onChange={(e) => setFormData(prev => ({ ...prev, hipaa_accepted: e.target.checked }))}
                disabled={submitting}
                required
                className="sr-only peer"
              />
              <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 rounded flex items-center justify-center peer-checked:bg-blue-600 peer-checked:border-blue-600 transition-colors flex-shrink-0 mt-0.5">
                {formData.hipaa_accepted && <Check className="w-3 h-3 text-white" />}
              </div>
              <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white">
                {t('auth.i_acknowledge_and_accept_the', 'I acknowledge and accept the')} <a href="/hipaa" target="_blank" className="text-blue-600 dark:text-blue-400 font-medium hover:underline">{t('auth.hipaa_compliance_agreement', 'HIPAA Compliance Agreement')}</a> *
              </span>
            </label>

            {/* HIPAA Note */}
            <div className="flex gap-3 p-3 bg-blue-50 dark:bg-blue-900/30 rounded-md mt-4">
              <AlertCircle className="w-4 h-4 text-blue-700 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs md:text-sm text-blue-800 dark:text-blue-200 leading-relaxed">
                By creating an account, you agree to comply with HIPAA regulations when handling
                protected health information (PHI). Both agreements are required to use this platform.
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            fullWidth
            disabled={submitting || !formData.terms_accepted || !formData.hipaa_accepted}
            loading={submitting}
            className="mt-6"
          >
            {submitting ? 'Creating Account...' : 'Create Account'}
          </Button>
        </form>

        {/* Footer Links */}
        <div className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
          <span>{t('auth.already_have_an_account', 'Already have an account?')}</span>
          {' '}
          <a href="/login" className="text-blue-600 dark:text-blue-400 font-medium hover:underline">
            {t('auth.sign_in', 'Sign in')}
          </a>
        </div>
      </div>
    </div>
  );
};
